import React from 'react';
import { Col, Divider, Row } from 'antd';
import { isEqual } from 'lodash';

import {
  AssignmentHistoryResponse,
  AssignmentHistoryDetailsModel,
} from 'api/adminApiClient';
import HistoryLog from '../../../HistoryLog';
import HistoryField from '../../../HistoryField';
import {
  getPartnerProfitSplit,
  getSplitTypeLabel,
} from 'utils/assignmentUtils';
import { formatNumberAsCurrency } from 'utils/numberUtils';

import styles from '../../../HistoryCard.module.less';

type Props = {
  historyId: string;
  historyDetails: AssignmentHistoryDetailsModel;
};

export default function SplitInformationDetailsCard(props: Props) {
  const { historyId, historyDetails } = props;
  const { oldData, newData } = historyDetails;

  const hasPrimaryFirstNameChanged = !isEqual(
    oldData?.splitPartnerInfo?.firstName || '',
    newData?.splitPartnerInfo?.firstName || '',
  );
  const hasPrimaryLastNameChanged = !isEqual(
    oldData?.splitPartnerInfo?.lastName || '',
    newData?.splitPartnerInfo?.lastName || '',
  );
  const hasPrimaryEmailChanged = !isEqual(
    oldData?.splitPartnerInfo?.email || '',
    newData?.splitPartnerInfo?.email || '',
  );
  const hasPrimaryPhoneNumberChanged = !isEqual(
    oldData?.splitPartnerInfo?.phoneNumber || '',
    newData?.splitPartnerInfo?.phoneNumber || '',
  );
  const hasPrimaryContactChanged =
    hasPrimaryLastNameChanged ||
    hasPrimaryFirstNameChanged ||
    hasPrimaryEmailChanged ||
    hasPrimaryPhoneNumberChanged;

  const hasSplitTypeChanged = !isEqual(
    oldData?.splitPartnerInfo?.splitType || '',
    newData?.splitPartnerInfo?.splitType || '',
  );
  const hasCompanyNameChanged = !isEqual(
    oldData?.splitPartnerInfo?.companyName || '',
    newData?.splitPartnerInfo?.companyName || '',
  );
  const hasSubcontractorRateChanged = !isEqual(
    oldData?.splitPartnerInfo?.subcontractorRate || '',
    newData?.splitPartnerInfo?.subcontractorRate || '',
  );
  const hasSplitPercentChanged = !isEqual(
    oldData?.splitPartnerInfo?.splitPercent || '',
    newData?.splitPartnerInfo?.splitPercent || '',
  );
  const hasSplitAmountChanged = !isEqual(
    oldData?.splitPartnerInfo?.splitAmount || '',
    newData?.splitPartnerInfo?.splitAmount || '',
  );
  const hasZipCodeChanged = !isEqual(
    oldData?.splitPartnerInfo?.zipCode || '',
    newData?.splitPartnerInfo?.zipCode || '',
  );
  const hasCountryChanged = !isEqual(
    oldData?.splitPartnerInfo?.country || '',
    newData?.splitPartnerInfo?.country || '',
  );
  const hasStateChanged = !isEqual(
    oldData?.splitPartnerInfo?.state ||
      oldData?.splitPartnerInfo?.internationalStateName ||
      '',
    newData?.splitPartnerInfo?.state ||
      newData?.splitPartnerInfo?.internationalStateName ||
      '',
  );
  const hasCityChanged = !isEqual(
    oldData?.splitPartnerInfo?.city ||
      oldData?.splitPartnerInfo?.internationalCityName ||
      '',
    newData?.splitPartnerInfo?.city ||
      newData?.splitPartnerInfo?.internationalCityName ||
      '',
  );
  const hasAddressChanged = !isEqual(
    oldData?.splitPartnerInfo?.address || '',
    newData?.splitPartnerInfo?.address || '',
  );

  const hasExistingPartnerChanged =
    hasSplitTypeChanged ||
    hasCompanyNameChanged ||
    hasSubcontractorRateChanged ||
    hasSplitPercentChanged ||
    hasSplitAmountChanged ||
    hasZipCodeChanged ||
    hasCountryChanged ||
    hasStateChanged ||
    hasCityChanged ||
    hasAddressChanged;

  const renderExistingPartnerSection = (
    assignment?: AssignmentHistoryResponse,
  ) => {
    if (!assignment) return null;

    const { splitPartnerInfo } = assignment;

    const sectionName = 'split-information';
    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Split information</div>
          </Col>
          {hasSplitTypeChanged && (
            <HistoryField
              title="Split"
              value={getSplitTypeLabel(splitPartnerInfo?.splitType)}
              dataId={`${sectionName}-split-type-field`}
            />
          )}
          {hasCompanyNameChanged && (
            <HistoryField
              title="Member split company"
              value={splitPartnerInfo?.companyName}
              dataId={`${sectionName}-company-name-field`}
            />
          )}
          {(hasSplitAmountChanged || hasSplitPercentChanged) && (
            <HistoryField
              title="Split amount"
              value={getPartnerProfitSplit(
                splitPartnerInfo?.splitPercent,
                splitPartnerInfo?.splitAmount,
              )}
              dataId={`${sectionName}-split-amount-field`}
            />
          )}
          {hasCompanyNameChanged && (
            <HistoryField
              title="Subcontractor rate"
              value={formatNumberAsCurrency({
                num: splitPartnerInfo?.subcontractorRate,
              })}
              dataId={`${sectionName}-subcontractor-rate-field`}
            />
          )}
          {hasZipCodeChanged && (
            <HistoryField
              title="ZIP"
              value={splitPartnerInfo?.zipCode}
              dataId={`${sectionName}-zip-code-field`}
            />
          )}
          {hasCountryChanged && (
            <HistoryField
              title="Country"
              value={splitPartnerInfo?.country}
              dataId={`${sectionName}-country-field`}
            />
          )}
          {hasStateChanged && (
            <HistoryField
              title="State"
              value={
                splitPartnerInfo?.state ||
                splitPartnerInfo?.internationalStateName
              }
              dataId={`${sectionName}-state-field`}
            />
          )}
          {hasCityChanged && (
            <HistoryField
              title="City"
              value={
                splitPartnerInfo?.city ||
                splitPartnerInfo?.internationalCityName
              }
              dataId={`${sectionName}-city-field`}
            />
          )}
          {hasAddressChanged && (
            <HistoryField
              title="Address"
              value={splitPartnerInfo?.address}
              dataId={`${sectionName}-address-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderPrimaryContactSection = (
    assignment?: AssignmentHistoryResponse,
  ) => {
    if (!assignment) return null;

    const { splitPartnerInfo } = assignment;

    const sectionName = 'primary-contact';
    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Primary contact</div>
          </Col>
          {hasPrimaryFirstNameChanged && (
            <HistoryField
              title="First name"
              value={splitPartnerInfo?.firstName}
              dataId={`${sectionName}-first-name-field`}
            />
          )}
          {hasPrimaryLastNameChanged && (
            <HistoryField
              title="Last name"
              value={splitPartnerInfo?.lastName}
              dataId={`${sectionName}-last-name-field`}
            />
          )}
          {hasPrimaryEmailChanged && (
            <HistoryField
              title="Email"
              value={splitPartnerInfo?.email}
              dataId={`${sectionName}-email-field`}
            />
          )}
          {hasPrimaryPhoneNumberChanged && (
            <HistoryField
              title="Phone number"
              value={splitPartnerInfo?.phoneNumber}
              dataId={`${sectionName}-phone-number-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };
  const renderSplitInfoSection = (assignment?: AssignmentHistoryResponse) => {
    return (
      <>
        {hasExistingPartnerChanged && renderExistingPartnerSection(assignment)}
        {hasPrimaryContactChanged && renderPrimaryContactSection(assignment)}
      </>
    );
  };

  return (
    <HistoryLog
      historyId={historyId}
      oldData={renderSplitInfoSection(oldData)}
      newData={renderSplitInfoSection(newData)}
    />
  );
}
